import { Button } from "@/components/ui/button";
import { Calendar, Users, BarChart4 } from "lucide-react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { getCurrentClientData } from "@/services/authServices";

type HeroProps = {
  onExploreClick?: () => void;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  isAuthenticated: boolean;
};

const Hero = ({
  onExploreClick,
  setIsAuthenticated,
  isAuthenticated,
}: HeroProps) => {
  const navigate = useNavigate();
  const loggedInUser = getCurrentClientData();

  useEffect(() => {
    // Check if user is authenticated
    if (loggedInUser.token) {
      setIsAuthenticated(true);
    }
  }, [loggedInUser]);

  return (
    <div className="relative">
      {/* Gradient Background */}
      <div className="absolute inset-0 hero-gradient opacity-90 z-0"></div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32 text-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Manage Your Events with Confidence
            </h1>
            <p className="text-lg md:text-xl mb-8 text-blue-100">
              CecureStream Events provides everything you need to create,
              manage, and scale your virtual and hybrid events.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <Button
                size="lg"
                className="bg-white text-cecure-900 hover:bg-blue-100 font-semibold"
                onClick={() => {
                  if (isAuthenticated) {
                    navigate("/create");
                  } else {
                    navigate("/signin");
                  }
                }}
              >
                Create Your First Event
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={onExploreClick}
                className="border-white text-black hover:bg-white/10"
              >
                Explore Features
              </Button>
            </div>

            {/* Features list */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-blue-200" />
                </div>
                <div className="ml-4">
                  <h3 className="text-md font-medium">Event Management</h3>
                  <p className="mt-1 text-sm text-blue-200">
                    Create and manage your events with ease
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-200" />
                </div>
                <div className="ml-4">
                  <h3 className="text-md font-medium">Attendee Registration</h3>
                  <p className="mt-1 text-sm text-blue-200">
                    Custom forms with integrated payments
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <BarChart4 className="h-5 w-5 text-blue-200" />
                </div>
                <div className="ml-4">
                  <h3 className="text-md font-medium">Detailed Analytics</h3>
                  <p className="mt-1 text-sm text-blue-200">
                    Track and improve your events
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Image or illustration */}
          <div className="flex items-center justify-center">
            <div className="bg-white rounded-lg p-2 shadow-xl max-w-md w-full">
              <div className="bg-cecure-100 rounded overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80"
                  alt="Event management"
                  className="w-full h-auto object-cover"
                />
                <div className="p-4 bg-white">
                  <h4 className="font-medium text-lg text-cecure-900">
                    Tech Conference 2026
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Join us for the biggest tech event of the year
                  </p>
                  <div className="mt-4 flex justify-between items-center">
                    <span className="text-sm font-medium text-cecure-700">
                      Dec 12-14, 2026
                    </span>
                    <span className="px-2 py-1 bg-cecure-100 text-cecure-800 text-xs rounded-full">
                      250 Attendees
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
